import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import apiClient from '../libs/axios';

// Unico usuario autorizado para cambiar el correo de otros usuarios
const EMAIL_CHANGE_ADMIN = 'admin@crmred.co';

export function useChangePassword() {
    const store = useStore();
    const modalActive = ref(false);
    const isLoading = ref(false);
    const newPassword = ref('');
    const newEmail = ref('');
    const showPassword = ref(false);
    const errorMessage = ref('');
    const selectedUser = ref(null);

    const canChangeEmail = computed(() =>
        (store.getters['appUserdata/getUser']?.email || '').toLowerCase() === EMAIL_CHANGE_ADMIN
    );

    const openModal = (user) => {
        selectedUser.value = user;
        newPassword.value = '';
        newEmail.value = user?.email || '';
        errorMessage.value = '';
        showPassword.value = false;
        modalActive.value = true;
    };

    const closeModal = () => {
        modalActive.value = false;
        selectedUser.value = null;
        newPassword.value = '';
        newEmail.value = '';
        errorMessage.value = '';
        showPassword.value = false;
    };

    const submitChangePassword = async (onSuccess) => {
        const emailChanged = canChangeEmail.value
            && newEmail.value.trim().toLowerCase() !== (selectedUser.value?.email || '').toLowerCase();

        if (canChangeEmail.value && !newEmail.value.trim()) {
            errorMessage.value = 'El correo no puede estar vacío.';
            return;
        }

        if (!emailChanged && !newPassword.value) {
            errorMessage.value = canChangeEmail.value
                ? 'Indica un nuevo correo o una nueva contraseña.'
                : 'La contraseña debe tener al menos 6 caracteres.';
            return;
        }

        if (newPassword.value && newPassword.value.length < 6) {
            errorMessage.value = 'La contraseña debe tener al menos 6 caracteres.';
            return;
        }

        isLoading.value = true;
        errorMessage.value = '';

        try {
            const user = selectedUser.value;
            const payload = { user_id: user.id };
            if (newPassword.value) payload.new_password = newPassword.value;
            if (emailChanged) payload.new_email = newEmail.value.trim();

            const response = await apiClient.post('/v2/admin/change-password', payload);

            if (emailChanged) Object.assign(user, { email: response.data?.data?.email || payload.new_email });
            if (onSuccess) onSuccess(response.data.message || 'Datos actualizados correctamente.');
            closeModal();
        } catch (error) {
            const data = error?.response?.data;
            const errors = data?.errorMessage;
            const firstError = errors && typeof errors === 'object' ? Object.values(errors).flat()[0] : null;
            const message = typeof data?.data === 'string' ? data.data : data?.message;
            errorMessage.value = firstError || message || 'Error al actualizar el usuario.';
        } finally {
            isLoading.value = false;
        }
    };

    return {
        modalActive,
        isLoading,
        newPassword,
        newEmail,
        showPassword,
        errorMessage,
        selectedUser,
        canChangeEmail,
        openModal,
        closeModal,
        submitChangePassword,
    };
}
